import React from 'react';
import { InfoTooltip, useStyles } from '@webapps/styles';
import { DateRangeRounded, Share } from '@material-ui/icons';
import { Button } from '@material-ui/core';
import { useAuth, useQuery } from '@webapps/reactjs-tech-components';
import { searchOpcoBillings } from '@webapps/business';
import { useHistory } from 'react-router-dom';
import { ROUTES_PATH } from 'config/constants';
import { getBillingStatusChip, formatDate224 } from '@webapps/utils';
import theme from '../theme';
import Nodata from 'components/NoData';

export default function LastBillings() {
  const { classes, global, css } = useStyles(styles);

  const { idUser } = useAuth();
  const { push } = useHistory();

  const {
    images: { Billings }
  } = theme

  const { data: { content: billings = [], totalElements } = {}, refetch } = useQuery(searchOpcoBillings, {
    key: ['opcoBillings', idUser],
    variables: {
      id: idUser,
      size: 5,
      page: 0
    },
    keepPreviousData: true
  });

  return (
    <div className={classes.container}>
      <div className={classes.card}>
        <div className={classes.header}>
          <div className={global.title}>5 dernières factures</div>
          <InfoTooltip
            position="bottom"
            forceVisible={false}
            title="Comment ça marche ?"
            description="Affiche les 5 dernières factures générées, avec leur montant et leur date d'envoi."
            link={{ text: "En savoir plus" }}
          />
        </div>
        <div className={classes.billList}>
          {billings?.length > 0 ? (
            billings?.map((info) => (
              <div className={classes.billCard}>
                <div className={classes.billInfo}>
                  <div className={classes.amount}>{info?.opcoBilling?.lines?.reduce((sum, line) => sum + (line.amount || 0), 0)}€</div>
                  <div className={classes.billId}>
                    <div className={classes.billIdText}>{`N° de facture ${info?.opcoBilling?.internalBillingId}`} </div>
                  </div>
                  <div className={classes.meta}>
                    <DateRangeRounded className={classes.metaIcon} />
                    <div className={classes.date}>{`Envoyé le ${formatDate224(info?.opcoBilling?.sentAt)}`}</div>
                  </div>
                </div>
                {getBillingStatusChip(info?.opcoBilling)}
              </div>
            ))
          ) : (
            <Nodata
              image={Billings}
              contentWidth="70%"
              padding="0px 20px 20px 0px"
              title="Aucune facture trouvée"
              description="Les 5 dernières factures s'afficheront ici."
            />
          )}
        </div>
      </div>
      {(billings?.length > 0) && (
        <Button className={classes.viewAllButton} onClick={() => push(ROUTES_PATH.CFA_ACCOUNTING)}>
          <p className={classes.viewAllText}>Tout voir</p>
        </Button>
      )}
    </div>
  );
}

const styles = ({ spacing }) => ({
  container: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    alignItems: 'flex-start',
    backgroundColor: '#ffffff',
    borderRadius: '10px',
    flex: 1,
    padding: '20px',
    gap: '20px',
    overflow: 'hidden',
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    gap: '6px',
    overflow: 'visible',
  },
  title: {
    fontFamily: '"Montserrat", sans-serif',
    fontSize: '15px',
    fontWeight: 600,
    lineHeight: '15px',
    color: '#152652',
  },
  shareIcon: {
    height: '16px',
    width: '16px',
    fill: '#a8caff',
  },
  billList: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    flex: 1,
    overflow: 'hidden',
    minHeight: 0,
  },
  billCard: {
    alignItems: 'center',
    cursor: 'pointer',
    backgroundColor: 'transparent',
    display: 'flex',
    flexDirection: 'row',
    borderRadius: '10px',
    justifyContent: 'space-between',
    padding: '16px',
    width: '100%',
    '&:hover': {
      backgroundColor: '#f3f7ff',
      '& p': {
        color: '#0060f9',
      },
    },
    '@media (max-width: 1250px)': {
      flexDirection: 'column',
      gap: '6px',
      alignItems: 'flex-start',
    },
    '@media (max-width: 1024px)': {
      flexDirection: 'row',
      gap: 'initial',
      alignItems: 'center'
    }
  },
  billInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    overflow: 'hidden',
    flex: 1,
    minWidth: 0,
  },
  amount: {
    fontFamily: '"Montserrat", sans-serif',
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: '14px',
    color: '#152652',
    margin: '0px',
  },
  billId: {
    backgroundColor: '#0060f9',
    borderRadius: '5px',
    display: 'flex',
    padding: '4px 8px',
    width: 'fit-content',
  },
  billIdText: {
    fontFamily: '"Roboto", sans-serif',
    fontSize: '12px',
    lineHeight: '12px',
    textAlign: 'center',
    color: '#ffffff',
  },
  meta: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    gap: '4px',
    overflow: 'hidden',
  },
  metaIcon: {
    height: '14px',
    width: '14px',
    fill: '#5d75a8',
  },
  date: {
    fontFamily: '"Roboto", sans-serif',
    fontSize: '12px',
    lineHeight: '12px',
    color: '#5d75a8',
  },
  status: {
    backgroundColor: '#dbfad8',
    borderRadius: '10px',
    display: 'flex',
    padding: '16px',
    width: '162px',
  },
  statusText: {
    fontFamily: '"Roboto", sans-serif',
    fontSize: '13.5px',
    lineHeight: '13.5px',
    textAlign: 'center',
    color: '#008000',
    width: '100%',
  },
  viewAllButton: {
    backgroundColor: '#ffffff',
    border: '1px solid #c0d9ff',
    borderRadius: '0px',
    borderBottomLeftRadius: '10px',
    borderBottomRightRadius: '10px',
    display: 'flex',
    flexDirection: 'row',
    height: '40px',
    padding: '10px',
    width: '100%',
    '&:hover': {
      backgroundColor: '#ffffff',
      '& p': {
        color: '#152652',
      },
    },
  },
  viewAllText: {
    fontFamily: '"Montserrat", sans-serif',
    fontSize: '13px',
    fontWeight: 500,
    lineHeight: '13.5px',
    textAlign: 'center',
    color: '#5d75a8',
    margin: 0,
  },
});